// /use-cases/:slug — who Bumpups is for, shown on a real video (data in useCaseData.js)
import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Seo, { SITE_URL } from '../site/Seo';
import GoogleButton from '../site/GoogleButton';
import CtaBand from '../site/CtaBand';
import Faq from '../site/Faq';
import DemoCard from './DemoCard';
import { USE_CASE_PAGES, useCasePage } from './useCaseData';
import { EXAMPLES } from '../../myWorkspace/onboarding/examples';
import CHAPTERS from './exampleChapters.json';
import './useCases.css';

export const FREE_NOTE = '60 free minutes every month · First upload free to preview · No credit card needed';

export function StartButtons() {
  return (
    <div className="mk-actions mk-actions--center">
      <Link to="/signup" className="mk-btn mk-btn--primary">
        Start free <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
      </Link>
      <GoogleButton />
    </div>
  );
}

export default function UseCasePage() {
  const { slug } = useParams();
  const page = useCasePage(slug);
  if (!page) return <Navigate to="/use-cases" replace />;
  const ex = EXAMPLES[page.example.id];
  const others = USE_CASE_PAGES.filter(p => p.slug !== page.slug);

  return (
    <>
      <Seo
        title={page.seo.title}
        description={page.seo.description}
        jsonLd={{
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Use cases', item: `${SITE_URL}/use-cases` },
            { '@type': 'ListItem', position: 2, name: page.label, item: `${SITE_URL}/use-cases/${page.slug}` },
          ],
        }}
      />

      <section className="uc-hero mk-hero-bg">
        <div className="mk-container">
          <p className="mk-eyebrow"><FontAwesomeIcon icon={page.icon} /> {page.label}</p>
          <h1 className="mk-h1 uc-title">{page.h1}</h1>
          <p className="mk-lead">{page.lead}</p>
          <StartButtons />
          <p className="mk-note">{FREE_NOTE}</p>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head">
            <p className="mk-eyebrow">Real output</p>
            <h2 className="mk-h2">See it on a real video</h2>
            <p className="mk-lead">The chapters Bumpups wrote for a public video, and its answer to one question.</p>
          </div>
          <DemoCard
            videoId={ex.videoId}
            title={ex.title}
            by={ex.by}
            seconds={ex.seconds}
            chapters={CHAPTERS[page.example.id]}
            question={page.example.question}
            answer={page.example.answer}
          />
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head"><h2 className="mk-h2">How it works</h2></div>
          <div className="mk-grid mk-grid--3 uc-steps">
            {page.steps.map(([title, text], i) => (
              <div key={title} className="mk-card">
                <span className="uc-step-num">{i + 1}</span>
                <h3 className="mk-h3">{title}</h3>
                <p className="mk-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head">
            <h2 className="mk-h2">Ask it anything</h2>
            <p className="mk-lead">A few of the questions people ask. Every answer points to the moment in the video.</p>
          </div>
          <div className="uc-prompts">
            {page.prompts.map(p => <span key={p} className="uc-prompt">{p}</span>)}
          </div>
        </div>
      </section>

      <Faq title={`${page.label}: common questions`} items={page.faq} />

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <h2 className="mk-h3" style={{ marginBottom: 16 }}>Bumpups is also used for</h2>
          <div className="mk-grid mk-grid--3">
            {others.map(p => (
              <Link key={p.slug} to={`/use-cases/${p.slug}`} className="mk-card uc-card">
                <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                <h3 className="mk-h3">{p.label}</h3>
                <p className="mk-text">{p.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title={`Try it on your own ${page.slug === 'youtube-creators' ? 'video' : 'recording'}`} text={FREE_NOTE} />
    </>
  );
}
